import { ComponentType, createElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import ErrorBoundary from './ErrorBoundary';

/**
 * Registr React komponent dostupných pro mount z Latte šablon.
 * Novou komponentu zaregistruj v assets/js/react.tsx.
 */
const registry = new Map<string, ComponentType<any>>();

/** Aktivní rooty, aby opakovaný mount (např. po AJAX snippetu) nevytvářel duplicity. */
const roots = new WeakMap<Element, Root>();

export function register(name: string, component: ComponentType<any>): void {
    registry.set(name, component);
}

/**
 * Najde všechny elementy s data-react-component a připojí na ně React.
 * Props se předávají jako JSON v data-props.
 *
 * Použití v Latte:
 *   <div data-react-component="HelloReact" data-props='{"name": "světe"}'></div>
 *
 * Po překreslení AJAX snippetu zavolej window.ReactMount.mountAll(snippetElement).
 */
export function mountAll(scope: ParentNode = document): void {
    const elements = scope.querySelectorAll<HTMLElement>('[data-react-component]');
    elements.forEach((el) => {
        const name = el.dataset.reactComponent;
        if (!name) {
            return;
        }

        const component = registry.get(name);
        if (!component) {
            console.warn(`[react] Komponenta "${name}" není zaregistrovaná (assets/js/react.tsx).`);
            return;
        }

        let props: Record<string, unknown> = {};
        if (el.dataset.props) {
            try {
                props = JSON.parse(el.dataset.props);
            } catch (e) {
                console.error(`[react] Neplatný JSON v data-props u komponenty "${name}".`, e);
                return;
            }
        }

        let root = roots.get(el);
        if (!root) {
            root = createRoot(el);
            roots.set(el, root);
        }
        root.render(createElement(
            ErrorBoundary,
            {
                name,
                translationsUrl: typeof props.translationsUrl === 'string' ? props.translationsUrl : null,
            },
            createElement(component, props),
        ));
    });
}

/** Odpojí React z elementů uvnitř scope (např. před odstraněním snippetu z DOM). */
export function unmountAll(scope: ParentNode = document): void {
    const elements = scope.querySelectorAll<HTMLElement>('[data-react-component]');
    elements.forEach((el) => {
        const root = roots.get(el);
        if (root) {
            root.unmount();
            roots.delete(el);
        }
    });
}
